import { useState, useRef, useEffect } from "react";
import { fileUrl } from "@/lib/api";
import { ChevronLeft, ChevronRight, Maximize2, X, CheckCircle2 } from "lucide-react";

export default function PptViewer({ slidePaths = [], completed, onComplete }) {
  const [idx, setIdx] = useState(0);
  const [fs, setFs] = useState(false);
  const [portrait, setPortrait] = useState(false);
  const frameRef = useRef(null);
  const total = slidePaths.length;

  // Track device orientation (for landscape fallback when OS lock is unavailable, e.g. iOS)
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(orientation: portrait)");
    const update = () => setPortrait(mq.matches);
    update();
    mq.addEventListener ? mq.addEventListener("change", update) : mq.addListener(update);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", update) : mq.removeListener(update));
  }, []);

  // Sync state when the user leaves native fullscreen (ESC / back button)
  useEffect(() => {
    const onFsChange = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        setFs(false);
        try { window.screen?.orientation?.unlock?.(); } catch {}
      }
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
    };
  }, []);

  const enterFullscreen = async () => {
    setFs(true);
    const el = frameRef.current;
    try {
      if (el?.requestFullscreen) await el.requestFullscreen();
      else if (el?.webkitRequestFullscreen) el.webkitRequestFullscreen();
    } catch {}
    try {
      await window.screen?.orientation?.lock?.("landscape");
    } catch {}
  };

  const exitFullscreen = async () => {
    try {
      if (document.fullscreenElement && document.exitFullscreen) await document.exitFullscreen();
      else if (document.webkitFullscreenElement && document.webkitExitFullscreen) document.webkitExitFullscreen();
    } catch {}
    try { window.screen?.orientation?.unlock?.(); } catch {}
    setFs(false);
  };

  if (!total) {
    return (
      <div className="text-sm text-[#434654] p-4 border border-[#E5E7EB] rounded-lg bg-white" data-testid="ppt-empty">
        No presentation uploaded yet.
      </div>
    );
  }

  const src = fileUrl(slidePaths[idx]);
  const atEnd = idx >= total - 1;

  const next = () => {
    const n = Math.min(total - 1, idx + 1);
    setIdx(n);
    if (n === total - 1 && !completed && onComplete) onComplete();
  };
  const prev = () => setIdx(Math.max(0, idx - 1));

  // Landscape fallback: if the device stays portrait (OS lock unsupported), rotate the slide 90°.
  const rotate = fs && portrait;
  const imgStyle = rotate
    ? { transform: "rotate(90deg)", width: "100vh", height: "100vw", maxWidth: "none", objectFit: "contain" }
    : { maxWidth: "100vw", maxHeight: "100vh", objectFit: "contain" };

  return (
    <div ref={frameRef} data-testid="ppt-viewer">
      {/* Inline card view */}
      {!fs && (
        <div className="rounded-lg border border-[#E5E7EB] bg-white overflow-hidden">
          <div className="relative bg-black flex items-center justify-center">
            <img src={src} alt={`Slide ${idx + 1}`} className="w-full object-contain max-h-[420px]" data-testid="ppt-slide-image" />
            <button
              onClick={enterFullscreen}
              data-testid="ppt-fullscreen-btn"
              className="absolute top-2 right-2 bg-white/90 rounded-md px-3 h-9 flex items-center gap-1.5 text-sm font-semibold text-[#1c1b1b] hover:bg-white transition-colors"
            >
              <Maximize2 className="w-4 h-4" /> Present
            </button>
          </div>
          <div className="flex items-center justify-between px-3 py-2 border-t border-[#E5E7EB]">
            <button onClick={prev} disabled={idx === 0} data-testid="ppt-prev-btn"
              className="flex items-center gap-1 text-sm font-semibold text-[#003d9b] disabled:opacity-40 h-11 px-2">
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <span className="text-sm font-semibold text-[#434654]" data-testid="ppt-slide-counter">
              Slide {idx + 1} / {total}
            </span>
            {!atEnd ? (
              <button onClick={next} data-testid="ppt-next-btn"
                className="flex items-center gap-1 text-sm font-semibold text-[#003d9b] h-11 px-2">
                Next <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <span className="flex items-center gap-1 text-sm font-semibold text-[#004e33]" data-testid="ppt-complete-indicator">
                <CheckCircle2 className="w-4 h-4" /> End
              </span>
            )}
          </div>
        </div>
      )}

      {/* Fullscreen presentation overlay */}
      {fs && (
        <div className="fixed inset-0 z-[100] bg-black flex flex-col select-none" data-testid="ppt-fullscreen">
          <div className="flex-1 flex items-center justify-center overflow-hidden">
            <img src={src} alt={`Slide ${idx + 1}`} style={imgStyle} data-testid="ppt-fullscreen-image" />
          </div>

          <button onClick={exitFullscreen} data-testid="ppt-exit-fullscreen-btn"
            className="absolute top-4 right-4 z-10 bg-white/90 rounded-full w-11 h-11 flex items-center justify-center hover:bg-white transition-colors">
            <X className="w-5 h-5 text-[#1c1b1b]" />
          </button>

          <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-4 py-3 bg-black/60 backdrop-blur-sm">
            <button onClick={prev} disabled={idx === 0} data-testid="ppt-fs-prev-btn"
              className="flex items-center gap-1 text-white font-semibold disabled:opacity-40 h-11 px-3">
              <ChevronLeft className="w-5 h-5" /> Prev
            </button>
            <span className="text-white font-semibold text-sm">Slide {idx + 1} / {total}</span>
            {!atEnd ? (
              <button onClick={next} data-testid="ppt-fs-next-btn"
                className="flex items-center gap-1 text-white font-semibold h-11 px-3">
                Next <ChevronRight className="w-5 h-5" />
              </button>
            ) : (
              <span className="flex items-center gap-1 text-[#5ee0a8] font-semibold h-11 px-3">
                <CheckCircle2 className="w-5 h-5" /> End
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
